import {connect} from "react-redux";
import {css} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {calculateRemSize, ml, mr, p} from "@pg-design/helpers-css-strings";
import {AreaIcon, FiltersIcon} from "@pg-design/icons-module";
import {IRange} from "@pg-design/inputs-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {form, FormActions, IFormProps} from "@pg-mono/form";
import {areaFormat} from "@pg-mono/string-utils";

import {IRPStore} from "../../app/rp_reducer";
import {getRoomsMessage} from "../../offer/list/utils/get_rooms_message";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {IPropertyConfiguratorSearchFiltersFormValues, propertyConfiguratorSearchFormFields} from "../reducers/property_configurator_reducer";
import {Floor} from "../types/Floor";
import {KitchenType} from "../types/KitchenType";
import {PropertyConfiguratorSearchFormValuesType} from "../types/PropertyConfiguratorSearchFormValuesType";
import {FormAdditionalAreaType, FormRoomType} from "../types/RoomType";

interface IOwnProps {
    onEditClick: (value: boolean) => void;
}

interface IStateProps {
    formValues: PropertyConfiguratorSearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, unknown>>;
}

interface IProps extends IStateProps, IActionsProps, IFormProps<IPropertyConfiguratorSearchFiltersFormValues>, IOwnProps {}

export const FiltersInfoBarC = ({onEditClick, ...props}: IProps) => {
    const getKitchenMessage = (values: KitchenType[]) => {
        if (values.length === 2) {
            return "Kuchnia zamknięta, Aneks kuchenny";
        }
        if (values.includes(KitchenType.CLOSED)) {
            return "Kuchnia zamknięta";
        }

        if (values.includes(KitchenType.KITCHENETTE)) {
            return "Aneks kuchenny";
        }
        return "";
    };

    const getAdditionalSpaces = (values: (FormAdditionalAreaType | FormRoomType)[]) => {
        const filteredValues = values.filter((value) => value.includes("as_"));
        if (filteredValues.length === 2) {
            return "Balkon lub taras, Ogródek";
        }

        if (filteredValues.includes(FormAdditionalAreaType.BALCONY)) {
            return "Balkon lub taras";
        }

        if (filteredValues.includes(FormAdditionalAreaType.GARDEN)) {
            return "Ogródek";
        }
        return "";
    };

    const getAreaMessage = ({lower, upper}: IRange<string>) => {
        if (!lower && !upper) {
            return "";
        }

        return areaFormat([parseInt(lower), parseInt(upper)]);
    };

    const getFloorMessage = (value: Floor[]) => {
        const sortList = [Floor.GROUND, Floor.LOWER, Floor.UPPER, Floor.TOP];
        const valueSorted = [...value].sort((a, b) => sortList.indexOf(a) - sortList.indexOf(b));
        const floors = {
            [Floor.GROUND]: "Parter",
            [Floor.LOWER]: "Niskie piętra (1-4)",
            [Floor.UPPER]: "Wysokie piętra (5+)",
            [Floor.TOP]: "Ostatnie piętro"
        };

        if (valueSorted[0] === valueSorted[1] || value.length === 1) {
            return floors[valueSorted[0]];
        }

        return `${floors[valueSorted[0]]} - ${floors[valueSorted[valueSorted.length - 1]]}`;
    };

    const getWorldDirectionMessage = (options: IValueLabel[], values: string[]) => {
        return (values || []).map((value) => options.find((direction) => direction.value.toString() === value)?.label).join(", ");
    };

    const roomsProps = props.getFieldProps("rooms");
    const areaProps = props.getFieldProps("area");
    const floorProps = props.getFieldProps("floors");
    const worldDirectionsProps = props.getFieldProps("world_directions");
    const kitchenTypeProps = props.getFieldProps("kitchen_type");
    const roomTypesProps = props.getFieldProps("rooms_area_type_selected");
    const kitchenMessage = getKitchenMessage(kitchenTypeProps.value);
    const roomsMessage = getRoomsMessage(roomsProps.value);
    const areaMessage = getAreaMessage(areaProps.value);
    const floorMessage = getFloorMessage(floorProps.value);
    const worldDirectionsMessage = getWorldDirectionMessage(props.propertyQuarters || [], worldDirectionsProps.value);
    const additionalSpacesMessage = getAdditionalSpaces(roomTypesProps.value);
    const upperMessages = [...(!!roomsMessage ? [roomsMessage] : []), ...(!!areaMessage ? [areaMessage] : [])].join(", ");
    const lowerMessages = [
        ...(!!additionalSpacesMessage ? [additionalSpacesMessage] : []),
        ...(!!kitchenMessage ? [kitchenMessage] : []),
        ...(!!floorMessage ? [floorMessage] : []),
        ...(!!worldDirectionsMessage ? [worldDirectionsMessage] : [])
    ].join(", ");

    return (
        <div css={summary}>
            <div css={summaryMessage}>
                <AreaIcon size="3.2" wrapperSize="3.2" wrapperColor="transparent" />
                <div css={summaryText}>
                    {!upperMessages && !lowerMessages && "Określ parametry"}
                    {upperMessages}
                    {!!upperMessages && !!lowerMessages && (
                        <>
                            , <br />
                        </>
                    )}
                    {lowerMessages}
                </div>
            </div>
            <Button variant="filled_primary" size="small" onClick={() => onEditClick(true)} css={btnStyle}>
                <FiltersIcon size="2.0" wrapperSize="2.0" css={filterIconStyle} />
                Określ parametry
            </Button>
        </div>
    );
};

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

export const FiltersInfoBar = connect(mapStateToProps)(form<IPropertyConfiguratorSearchFiltersFormValues, IProps>(FiltersInfoBarC));

const summary = css`
    min-height: ${calculateRemSize(7)};
    background-color: white;
    width: 100vw;
    ${p(1.5)};
    align-self: center;
    display: flex;
    align-items: start;
    flex-direction: column;
    gap: ${calculateRemSize(2)};

    @media (max-width: ${getThemeBreakpoint().md}) {
        align-items: center;
    }
`;

const summaryMessage = css`
    display: flex;
    align-items: center;
    width: 100%;
`;

const filterIconStyle = css`
    ${mr(1)};
`;

const btnStyle = css`
    width: 100%;
`;

const summaryText = css`
    font-size: 1.2rem;
    line-height: ${calculateRemSize(2)};
    font-weight: 700;
    ${ml(1)};
`;
import {connect} from "react-redux";
import {css} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {calculateRemSize, ml, mr, p} from "@pg-design/helpers-css-strings";
import {AreaIcon, FiltersIcon} from "@pg-design/icons-module";
import {IRange} from "@pg-design/inputs-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {form, FormActions, IFormProps} from "@pg-mono/form";
import {areaFormat} from "@pg-mono/string-utils";

import {IRPStore} from "../../app/rp_reducer";
import {getRoomsMessage} from "../../offer/list/utils/get_rooms_message";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {IPropertyConfiguratorSearchFiltersFormValues, propertyConfiguratorSearchFormFields} from "../reducers/property_configurator_reducer";
import {Floor} from "../types/Floor";
import {KitchenType} from "../types/KitchenType";
import {PropertyConfiguratorSearchFormValuesType} from "../types/PropertyConfiguratorSearchFormValuesType";
import {FormAdditionalAreaType, FormRoomType} from "../types/RoomType";

interface IOwnProps {
    onEditClick: (value: boolean) => void;
}

interface IStateProps {
    formValues: PropertyConfiguratorSearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, unknown>>;
}

interface IProps extends IStateProps, IActionsProps, IFormProps<IPropertyConfiguratorSearchFiltersFormValues>, IOwnProps {}

export const FiltersInfoBarC = ({onEditClick, ...props}: IProps) => {
    const getKitchenMessage = (values: KitchenType[]) => {
        if (values.length === 2) {
            return "Kuchnia zamknięta, Aneks kuchenny";
        }
        if (values.includes(KitchenType.CLOSED)) {
            return "Kuchnia zamknięta";
        }

        if (values.includes(KitchenType.KITCHENETTE)) {
            return "Aneks kuchenny";
        }
        return "";
    };

    const getAdditionalSpaces = (values: (FormAdditionalAreaType | FormRoomType)[]) => {
        const filteredValues = values.filter((value) => value.includes("as_"));
        if (filteredValues.length === 2) {
            return "Balkon lub taras, Ogródek";
        }

        if (filteredValues.includes(FormAdditionalAreaType.BALCONY)) {
            return "Balkon lub taras";
        }

        if (filteredValues.includes(FormAdditionalAreaType.GARDEN)) {
            return "Ogródek";
        }
        return "";
    };

    const getAreaMessage = ({lower, upper}: IRange<string>) => {
        if (!lower && !upper) {
            return "";
        }

        return areaFormat([parseInt(lower), parseInt(upper)]);
    };

    const getFloorMessage = (value: Floor[]) => {
        const sortList = [Floor.GROUND, Floor.LOWER, Floor.UPPER, Floor.TOP];
        const valueSorted = [...value].sort((a, b) => sortList.indexOf(a) - sortList.indexOf(b));
        const floors = {
            [Floor.GROUND]: "Parter",
            [Floor.LOWER]: "Niskie piętra (1-4)",
            [Floor.UPPER]: "Wysokie piętra (5+)",
            [Floor.TOP]: "Ostatnie piętro"
        };

        if (valueSorted[0] === valueSorted[1] || value.length === 1) {
            return floors[valueSorted[0]];
        }

        return `${floors[valueSorted[0]]} - ${floors[valueSorted[valueSorted.length - 1]]}`;
    };

    const getWorldDirectionMessage = (options: IValueLabel[], values: string[]) => {
        return (values || []).map((value) => options.find((direction) => direction.value.toString() === value)?.label).join(", ");
    };

    const roomsProps = props.getFieldProps("rooms");
    const areaProps = props.getFieldProps("area");
    const floorProps = props.getFieldProps("floors");
    const worldDirectionsProps = props.getFieldProps("world_directions");
    const kitchenTypeProps = props.getFieldProps("kitchen_type");
    const roomTypesProps = props.getFieldProps("rooms_area_type_selected");
    const kitchenMessage = getKitchenMessage(kitchenTypeProps.value);
    const roomsMessage = getRoomsMessage(roomsProps.value);
    const areaMessage = getAreaMessage(areaProps.value);
    const floorMessage = getFloorMessage(floorProps.value);
    const worldDirectionsMessage = getWorldDirectionMessage(props.propertyQuarters || [], worldDirectionsProps.value);
    const additionalSpacesMessage = getAdditionalSpaces(roomTypesProps.value);
    const upperMessages = [...(!!roomsMessage ? [roomsMessage] : []), ...(!!areaMessage ? [areaMessage] : [])].join(", ");
    const lowerMessages = [
        ...(!!additionalSpacesMessage ? [additionalSpacesMessage] : []),
        ...(!!kitchenMessage ? [kitchenMessage] : []),
        ...(!!floorMessage ? [floorMessage] : []),
        ...(!!worldDirectionsMessage ? [worldDirectionsMessage] : [])
    ].join(", ");

    return (
        <div css={summary}>
            <div css={summaryMessage}>
                <AreaIcon size="3.2" wrapperSize="3.2" wrapperColor="transparent" />
                <div css={summaryText}>
                    {!upperMessages && !lowerMessages && "Określ parametry"}
                    {upperMessages}
                    {!!upperMessages && !!lowerMessages && (
                        <>
                            , <br />
                        </>
                    )}
                    {lowerMessages}
                </div>
            </div>
            <Button variant="filled_primary" size="small" onClick={() => onEditClick(true)} css={btnStyle}>
                <FiltersIcon size="2.0" wrapperSize="2.0" css={filterIconStyle} />
                Określ parametry
            </Button>
        </div>
    );
};

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

export const FiltersInfoBar = connect(mapStateToProps)(form<IPropertyConfiguratorSearchFiltersFormValues, IProps>(FiltersInfoBarC));

const summary = css`
    min-height: ${calculateRemSize(7)};
    background-color: white;
    width: 100vw;
    ${p(1.5)};
    align-self: center;
    display: flex;
    align-items: start;
    flex-direction: column;
    gap: ${calculateRemSize(2)};

    @media (max-width: ${getThemeBreakpoint().md}) {
        align-items: center;
    }
`;

const summaryMessage = css`
    display: flex;
    align-items: center;
    width: 100%;
`;

const filterIconStyle = css`
    ${mr(1)};
`;

const btnStyle = css`
    width: 100%;
`;

const summaryText = css`
    font-size: 1.2rem;
    line-height: ${calculateRemSize(2)};
    font-weight: 700;
    ${ml(1)};
`;
